// Live agent-run SignalR connection for the copilot + the action-required inbox.
//
// One connection per authenticated session. Mounted INSIDE RequireAuth (see
// routes.tsx) so it only ever connects when signed in, and tears down on
// sign-out / unmount. Mirrors ActivityConnectionProvider; the server adds this
// connection to its own `agent-tenant:{id}` group on connect (derived from the
// auth cookie, never a client parameter).
//
// Reconnect semantics matter for the no-missed-alerts guarantee: every
// (re)connect bumps `connectionEpoch`, and a `KeepSessionHot` heartbeat keeps the
// server-side session-hot Redis flag alive so the agent runner stays on its tight
// poll cadence while a copilot session is open. The inbox reseeds the full
// pending set from the DB on each epoch bump — SignalR is only a refresh trigger.
//
// `observer` mode is for the app-shell "Action Required" badge, which is mounted on
// EVERY authenticated page: it connects with `?mode=observer` so the server adds it
// to the tenant group (live events) but skips the session-hot flag, and it sends no
// heartbeat. That keeps the badge real-time without pinning runners hot just because
// the app is open on an unrelated page — the reason the interactive provider stays
// scoped to the agent pages.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  type HubConnection,
} from '@microsoft/signalr';
import type { AgentActivityEvent } from '@shared/api-types';
import { AgentActivityContext, type AgentActivityContextValue } from './agentActivity';

/** Server-side SendAsync event name (matches SignalRAgentActivityNotifier.EventName). */
const AGENT_EVENT = 'AgentActivity';
/** Same-origin hub path — under /api/* so Front Door + the dev proxy route it. */
const HUB_URL = '/api/hubs/agent';
/** Heartbeat interval to refresh the server-side session-hot flag (TTL is 2m). */
const KEEPALIVE_MS = 45_000;

export function AgentConnectionProvider({
  children,
  observer = false,
}: {
  children: ReactNode;
  /** Listen-only: receive live events without pinning the runner hot (app-shell badge). */
  observer?: boolean;
}) {
  const connectionRef = useRef<HubConnection | null>(null);
  const [connected, setConnected] = useState(false);
  const [connectionEpoch, setConnectionEpoch] = useState(0);
  const handlersRef = useRef(new Set<(e: AgentActivityEvent) => void>());

  useEffect(() => {
    const connection = new HubConnectionBuilder()
      .withUrl(observer ? `${HUB_URL}?mode=observer` : HUB_URL, { withCredentials: true })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();
    connectionRef.current = connection;

    const markConnected = () => {
      setConnected(true);
      // Bump the epoch so consumers reseed from the DB on this fresh connection.
      setConnectionEpoch((n) => n + 1);
      // Refresh the session-hot flag immediately on (re)connect — but never for an
      // observer: it must not pin the runner hot.
      if (!observer) void connection.invoke('KeepSessionHot').catch(() => {});
    };

    connection.on(AGENT_EVENT, (e: AgentActivityEvent) => {
      handlersRef.current.forEach((h) => h(e));
    });
    connection.onreconnected(() => markConnected());
    connection.onclose(() => setConnected(false));

    let cancelled = false;
    const startPromise = connection
      .start()
      .then(() => {
        if (!cancelled) markConnected();
      })
      .catch(() => {
        // Best-effort: the inbox still works via its initial DB load + manual refresh.
        if (!cancelled) setConnected(false);
      });

    // Heartbeat keeps the server-side session-hot flag alive (tight runner cadence).
    // Observers send no heartbeat — they never mark the session hot.
    const keepAlive = observer
      ? undefined
      : window.setInterval(() => {
          if (connection.state === HubConnectionState.Connected) {
            void connection.invoke('KeepSessionHot').catch(() => {});
          }
        }, KEEPALIVE_MS);

    return () => {
      cancelled = true;
      if (keepAlive !== undefined) window.clearInterval(keepAlive);
      connection.off(AGENT_EVENT);
      void startPromise.finally(() => {
        void connection.stop();
      });
      connectionRef.current = null;
    };
  }, [observer]);

  const onAgentActivity = useCallback((handler: (e: AgentActivityEvent) => void) => {
    handlersRef.current.add(handler);
    return () => {
      handlersRef.current.delete(handler);
    };
  }, []);

  const value = useMemo<AgentActivityContextValue>(
    () => ({ connected, connectionEpoch, onAgentActivity }),
    [connected, connectionEpoch, onAgentActivity],
  );

  return <AgentActivityContext.Provider value={value}>{children}</AgentActivityContext.Provider>;
}
